import { Link } from 'react-router-dom';
import { formatMoney } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { usePosterOverview } from '@/features/poster/use-poster-overview';
import { PosterShell } from '@/features/poster/PosterShell';
import { useMyJobs } from '@/features/jobs/use-jobs';
import { EmptyState, StatTile } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@/components/ui/Icons';
import { ReviewPrompt } from '@/features/reviews/ReviewPrompt';
import { presentApiError } from '@/lib/form-errors';

/**
 * The employer's home. [Job Poster journey]
 *
 * Ordered by who is blocked: applicants waiting on a decision come first,
 * because a teen refreshing their phone is on the other end of that queue.
 */
export function PosterDashboardPage() {
  const { user } = useAuth();
  const overview = usePosterOverview();
  const recentJobs = useMyJobs({ pageSize: 5 });
  const data = overview.data;

  const firstName = user?.displayName.split(' ')[0] ?? 'there';
  const needsYou =
    (data?.newApplications ?? 0) +
    (data?.openBids ?? 0) +
    (data?.awaitingApproval ?? 0) +
    (data?.awaitingPayment ?? 0);

  return (
    <PosterShell
      title={`Welcome back, ${firstName}`}
      description={
        needsYou > 0
          ? `${needsYou} ${needsYou === 1 ? 'person is' : 'people are'} waiting on your decision.`
          : 'Nothing is waiting on you right now.'
      }
    >
      {overview.isPending && (
        <div className="space-y-6" aria-busy="true">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-40" />
        </div>
      )}

      {overview.isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(overview.error, 'Could not load your console').message}
          </p>
          <Button className="mt-5" onClick={() => void overview.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="space-y-8">
          {/* Renders nothing when there is no finished work to rate. */}
          <ReviewPrompt />

          {/* Waiting on you */}
          {needsYou > 0 && (
            <section className="rounded-2xl border border-sun-200 bg-sun-50 p-5 sm:p-6">
              <h2 className="text-base font-bold text-sun-800">Waiting on you</h2>
              <ul className="mt-4 space-y-2">
                {data.newApplications > 0 && (
                  <li>
                    <Link
                      to="/applicants?status=SUBMITTED"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.newApplications} new{' '}
                        {data.newApplications === 1 ? 'application' : 'applications'} to review
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
                {data.openBids > 0 && (
                  <li>
                    <Link
                      to="/applicants?tab=bids"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.openBids} open {data.openBids === 1 ? 'bid' : 'bids'} to review
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
                {data.awaitingApproval > 0 && (
                  <li>
                    <Link
                      to="/tracking"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.awaitingApproval}{' '}
                        {data.awaitingApproval === 1 ? 'job' : 'jobs'} finished and waiting on
                        your sign-off
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
                {data.awaitingPayment > 0 && (
                  <li>
                    <Link
                      to="/tracking"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.awaitingPayment} approved{' '}
                        {data.awaitingPayment === 1 ? 'job' : 'jobs'} still to be paid
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* Hiring pipeline */}
          <section>
            <h2 className="mb-4 text-base font-bold text-ink">Your hiring</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="New applications"
                value={data.newApplications}
                tone={data.newApplications > 0 ? 'attention' : 'neutral'}
                hint={data.newApplications > 0 ? 'Nobody has looked yet' : 'All reviewed'}
                icon={<UsersIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Shortlisted"
                value={data.shortlisted}
                hint="Ready to assign"
                tone={data.shortlisted > 0 ? 'good' : 'neutral'}
                icon={<BadgeCheckIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Teens assigned"
                value={data.teensAssigned}
                hint={
                  data.awaitingTeenResponse > 0
                    ? `${data.awaitingTeenResponse} yet to confirm`
                    : 'All confirmed'
                }
                tone={data.awaitingTeenResponse > 0 ? 'attention' : 'neutral'}
                icon={<BriefcaseIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Trusted by guardians"
                value={data.trustedByParents}
                hint="Parents who vouch for you"
                tone={data.trustedByParents > 0 ? 'good' : 'neutral'}
                icon={<ShieldCheckIcon className="size-4 text-slate-400" />}
              />
            </div>
          </section>

          {/* Money */}
          <section className="overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
            <div className="border-b border-line px-6 py-4">
              <h2 className="text-base font-bold text-ink">What you have committed</h2>
            </div>
            <dl className="grid grid-cols-1 divide-y divide-line sm:grid-cols-2 sm:divide-x sm:divide-y-0">
              <div className="px-6 py-5">
                <dt className="text-xs font-semibold text-slate-500">On work under way</dt>
                <dd className="mt-1 text-2xl font-extrabold text-ink">
                  {formatMoney(data.committedSpend.amountMinor, data.committedSpend.currency)}
                </dd>
                <p className="mt-1 text-xs text-slate-500">Agreed but not yet released</p>
              </div>
              <div className="px-6 py-5">
                <dt className="text-xs font-semibold text-slate-500">On finished work</dt>
                <dd className="mt-1 text-2xl font-extrabold text-safe-700">
                  {formatMoney(data.paidOut.amountMinor, data.paidOut.currency)}
                </dd>
                <p className="mt-1 text-xs text-slate-500">
                  Across {data.completedJobs} completed {data.completedJobs === 1 ? 'job' : 'jobs'}
                </p>
              </div>
            </dl>
          </section>

          {/* Jobs */}
          <section>
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-base font-bold text-ink">Your jobs</h2>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="safe">{data.liveJobs} live</Badge>
                {data.inProgressJobs > 0 && (
                  <Badge tone="brand">{data.inProgressJobs} in progress</Badge>
                )}
                {data.draftJobs > 0 && <Badge tone="neutral">{data.draftJobs} draft</Badge>}
                <Link
                  to="/my-jobs"
                  className="ml-1 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline"
                >
                  See all
                  <ArrowRightIcon className="size-4" />
                </Link>
              </div>
            </div>

            {recentJobs.data?.items.length === 0 ? (
              <EmptyState
                icon={<BriefcaseIcon className="size-6" />}
                title="No jobs posted yet"
                action={
                  <Link
                    to="/jobs/new"
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
                  >
                    Post your first job
                    <ArrowRightIcon className="size-4" />
                  </Link>
                }
              >
                Post a job and verified, guardian-approved teens can start applying straight away.
              </EmptyState>
            ) : (
              <ul className="space-y-3">
                {recentJobs.data?.items.map((job) => (
                  <li
                    key={job.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white p-4 shadow-soft transition hover:border-brand-200"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="text-sm font-bold text-ink hover:underline"
                      >
                        {job.title}
                      </Link>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                        <span>{job.category}</span>
                        <span className="font-semibold text-ink">
                          {formatMoney(job.pay.amountMinor, job.pay.currency)}
                        </span>
                        <span>
                          {job.teensAssigned}/{job.teensNeeded} filled
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={job.status === 'LIVE' ? 'safe' : 'neutral'}>
                        {job.status.charAt(0) + job.status.slice(1).toLowerCase().replace('_', ' ')}
                      </Badge>
                      <Link
                        to={`/applicants?jobId=${job.id}`}
                        className="rounded-xl border border-line bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-brand-300 hover:bg-brand-50"
                      >
                        Applicants
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </PosterShell>
  );
}
